/**
 * AgentModelModal — the ONE shared "Agent & model" editor.
 *
 * Used by BOTH surfaces that change an agent/model + credential:
 *   - PIPELINES (PipelineDetailPage): full agent:model SSOT picker
 *     (`AgentModelSelect` seeded from `useAgentOptions()`), Claude gets a
 *     separate API-key + OAuth-token field. Caller's onSave PATCHes
 *     /workflows/{uuid} with {aiAgent, aiModel, aiApiKey, aiOAuthToken}.
 *   - APPS (AppDetailPage): vendor-only select (Claude / Codex), single
 *     credential field. Caller's onSave POSTs Apps.setAgent({provider, token}).
 *
 * The two used to be separate components that drifted visually. This is the
 * single source of truth for the chrome (mkt-modal), the field styling
 * (atomic Field + apps-modal-field-label), the validation gate, and the
 * masked-credential handling. The PARENT owns the request shape via `onSave`.
 *
 * Credentials arrive MASKED from the GET (sk-a...wxyz). We seed the field
 * with the mask and treat a value still containing '...' as "untouched" —
 * never sending it. A real change (or a vendor switch that empties the seed)
 * is validated by format before submit.
 *
 * Validation (the hard requirement — empty/invalid must NOT save):
 *   - A token is REQUIRED when there's no stored masked token to keep for the
 *     selected agent/provider. Empty → inline "API key required", no submit.
 *   - A TYPED token (not the mask) is format-checked:
 *       claude  → ^sk-ant-(oat01|api03)-   (api03 = API key, oat01 = OAuth)
 *       codex   → ^sk-
 *   - Save is disabled while invalid; submit shows the inline error.
 *   - Server errors map onto the same inline field slot (short, red).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { IonIcon } from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import Field from '../../atoms/Field/Field';
import { parseAgentOption } from '../../../utils/agent-options';
import AgentModelFields, {
  validateToken, isMask, isClaudeOAuthToken, VENDOR_KEY_HINT, VENDOR_KEY_LABEL,
} from '../AgentModelFields/AgentModelFields';
// Chrome classes (mkt-modal-overlay / mkt-modal / header / title / close /
// body / deploy / secondary) come from the marketplace detail modal stylesheet.
import '../../../pages/MarketplacePage/MarketplaceDetailModal.css';
import '../../atoms/Field/Field.css';
import './AgentModelModal.css';

export default function AgentModelModal({
  isOpen,
  onClose,
  title = 'Model & engine',
  variant,
  agentOptions = [],
  seedValue = '',
  seedKey = '',
  seedOAuth = '',
  onSave,
  onSaved,
  setToast,
  // Optional "also set as this project's default" opt-in. Shown only when the
  // caller asks (deploy-time, project has no key). When checked, the saved
  // body carries `saveToProject: true` so the caller can persist the key to
  // the project too — not just to the agent/app row.
  showSaveToProject = false,
  saveToProjectLabel = 'Save as project default',
  // CTA label. Deploy-from-marketplace passes "Save and deploy"; default Save.
  submitLabel = 'Save',
}) {
  // `variant` decides the agent control: 'pipeline' = full agent:model
  // SSOT picker; 'app' = vendor-only (Claude / Codex) select.
  const isPipeline = variant === 'pipeline';

  // Coerce seeds to strings — callers pass row fields that can be null OR a
  // non-string (which would render as "[object Object]" in the input).
  const str = (x) => (typeof x === 'string' ? x : '');
  const [value, setValue] = useState(str(seedValue));          // pipeline: 'claude:opus-4.8'
  const [provider, setProvider] = useState(str(seedValue) || 'claude'); // app: 'claude'|'codex'
  // ONE credential field. Seed it from whichever credential the row has — an
  // API key OR a Pro/Max OAuth token — so a stored OAuth-only cred still shows
  // as "✓ Saved" instead of an empty field.
  const [apiKey, setApiKey] = useState(str(seedKey) || str(seedOAuth));
  const [saving, setSaving] = useState(false);
  const [keyErr, setKeyErr] = useState('');   // inline error on the key field
  const [submitted, setSubmitted] = useState(false);
  // Credential fields start readOnly so the browser can't autofill a saved
  // password into them (decoys + autoComplete aren't enough in Chrome). They
  // unlock on first focus — by then autofill has already passed.
  const [credUnlocked, setCredUnlocked] = useState(false);
  const [saveToProject, setSaveToProject] = useState(true);

  // (Re)seed on open / when the underlying values change.
  useEffect(() => {
    if (!isOpen) return;
    setValue(str(seedValue));
    setProvider(str(seedValue) || 'claude');
    setApiKey(str(seedKey) || str(seedOAuth));
    setCredUnlocked(false);
    setKeyErr('');
    setSubmitted(false);
    setSaving(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, seedValue, seedKey, seedOAuth]);

  // Selected vendor — drives which credential fields show + how we validate.
  const vendor = useMemo(() => {
    if (isPipeline) return parseAgentOption(value).aiAgent;
    return provider;
  }, [isPipeline, value, provider]);

  const isClaude = vendor === 'claude';
  // EVERY agent here is BYOK and needs a key — claude (sk-ant-…), cursor
  // (key_…), codex (sk-…), gemini (AIza…). Only the "project default" empty
  // option (no vendor) needs none.
  const needsKey = Boolean(vendor);

  // The masked seed we'd keep if the user doesn't type. Switching vendor away
  // from the seeded one empties it (the stored token belongs to the other
  // vendor) — so a fresh key becomes required for the new vendor.
  const seededVendor = isPipeline
    ? parseAgentOption(str(seedValue)).aiAgent
    : (str(seedValue) || 'claude');
  const maskedSeed = vendor === seededVendor ? (str(seedKey) || str(seedOAuth)) : '';

  // When the user switches vendor, the stored key (for the old vendor) no
  // longer applies — clear it (or restore the mask if they switched back to
  // the seeded vendor) so the field reflects what we'd actually use.
  const prevVendorRef = useRef(vendor);
  useEffect(() => {
    if (prevVendorRef.current === vendor) return;
    prevVendorRef.current = vendor;
    setApiKey(vendor === seededVendor ? (str(seedKey) || str(seedOAuth)) : '');
    setKeyErr('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendor]);

  // Live validity — gates the Save button (one credential field).
  const tokenError = needsKey
    ? validateToken({ vendor, value: apiKey, maskedSeed })
    : '';
  const isValid = !tokenError;

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, saving, onClose]);

  if (!isOpen) return null;

  const tokenIsMask = isMask(apiKey);

  const handleSave = async (e) => {
    e?.preventDefault?.();
    setSubmitted(true);
    if (!isValid) {
      // Surface the gating error inline (the field is already error-styled
      // because submitted flips on).
      setKeyErr(tokenError);
      return;
    }
    setKeyErr('');

    // Build the surface-specific body. Only send a credential the user
    // actually changed (differs from the masked seed AND isn't the mask).
    let body;
    if (isPipeline) {
      const { aiAgent, aiModel } = parseAgentOption(value);
      body = { aiAgent: aiAgent || null, aiModel: aiModel || null };
      // ONE field → route by prefix. A fresh entry replaces the active
      // credential and clears the other slot, so exactly one is in effect.
      if (needsKey && apiKey !== maskedSeed && !tokenIsMask) {
        const v = apiKey.trim();
        if (isClaude) {
          if (v === '') { body.aiApiKey = null; body.aiOAuthToken = null; }
          else if (isClaudeOAuthToken(v)) { body.aiOAuthToken = v; body.aiApiKey = null; }
          else { body.aiApiKey = v; body.aiOAuthToken = null; }
        } else {
          body.aiApiKey = v === '' ? null : v;
        }
      }
    } else {
      body = { provider };
      if (apiKey !== maskedSeed && !tokenIsMask && apiKey.trim() !== '') {
        body.token = apiKey.trim();
      }
    }
    if (showSaveToProject) body.saveToProject = saveToProject;

    setSaving(true);
    setKeyErr('');
    try {
      await onSave(body);
      setToast?.({ show: true, isOpen: true, message: 'Model updated', color: 'success' });
      onSaved?.();
      onClose?.();
    } catch (e2) {
      // Map the server error onto the field inline (short).
      setKeyErr(e2?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // The error shown on the key field: the live gate error after a submit
  // attempt, otherwise the server error (if any).
  const shownKeyErr = (submitted && tokenError) ? tokenError : keyErr;

  return createPortal(
    <div className="mkt-modal-overlay" role="presentation" onClick={() => !saving && onClose?.()}>
      <div
        className="mkt-modal agent-model-modal"
        style={{ maxWidth: 460 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="agent-model-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="mkt-modal-close"
          aria-label="Close"
          onClick={onClose}
          disabled={saving}
        >
          <IonIcon icon={closeOutline} />
        </button>

        <div className="mkt-modal-body">
          <header className="mkt-modal-header">
            <h2 className="mkt-modal-title" id="agent-model-title">{title}</h2>
          </header>

          <form onSubmit={handleSave} autoComplete="off" className="agent-model-form">
            {/* Off-screen decoys soak up the browser's saved-login autofill so
                it can't dump a saved password into the token field. */}
            <input type="text" name="zibby-decoy-user" tabIndex={-1} autoComplete="username"
              aria-hidden="true" style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }} />
            <input type="password" name="zibby-decoy-pass" tabIndex={-1} autoComplete="new-password"
              aria-hidden="true" style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }} />

            {isPipeline ? (
              // Pipeline / deploy / settings all render the ONE shared atomic
              // form so they look + behave identically.
              <AgentModelFields
                agentOptions={agentOptions}
                value={value}
                onValueChange={(v) => { setValue(v); setKeyErr(''); }}
                apiKey={apiKey}
                onApiKeyChange={(v) => { setApiKey(v); if (keyErr) setKeyErr(''); }}
                submitted={submitted}
                serverKeyError={keyErr}
                disabled={saving}
                showSaveToProject={showSaveToProject}
                saveToProject={saveToProject}
                onSaveToProjectChange={setSaveToProject}
                saveToProjectLabel={saveToProjectLabel}
              />
            ) : (
              // App variant: vendor-only (Claude/Codex) select + single key.
              <>
                <div className="agent-model-field">
                  <label className="apps-modal-field-label" htmlFor="agent-model-pick">Model &amp; engine</label>
                  <select
                    id="agent-model-pick"
                    className="mkt-field mkt-field--select"
                    value={provider}
                    onChange={(e) => { setProvider(e.target.value); setKeyErr(''); }}
                    disabled={saving}
                    aria-label="Model provider for this app"
                  >
                    <option value="claude">Claude</option>
                    <option value="codex">Codex</option>
                  </select>
                </div>
                {needsKey && (
                  <div className="agent-model-field">
                    <label className="apps-modal-field-label" htmlFor="agent-model-key">
                      {VENDOR_KEY_LABEL[vendor] || 'API key'}
                    </label>
                    <Field
                      as="input"
                      id="agent-model-key"
                      type="text"
                      name="zibby-agent-token"
                      value={apiKey}
                      placeholder={VENDOR_KEY_HINT[vendor] || 'API key'}
                      onChange={(e) => { setApiKey(e.target.value); if (keyErr) setKeyErr(''); }}
                      readOnly={!credUnlocked}
                      onFocus={() => setCredUnlocked(true)}
                      autoComplete="new-password"
                      data-1p-ignore="true"
                      data-lpignore="true"
                      spellCheck={false}
                      disabled={saving}
                      status={shownKeyErr ? 'error' : null}
                      aria-invalid={shownKeyErr ? true : undefined}
                    />
                    {shownKeyErr && <p className="agent-model-field-error">{shownKeyErr}</p>}
                  </div>
                )}
              </>
            )}

            <div className="mkt-alert-modal-actions">
              <button
                type="button"
                onClick={handleSave}
                className="mkt-modal-deploy mkt-modal-deploy--full"
                disabled={saving || !isValid}
              >
                {saving ? `${submitLabel}…` : submitLabel}
              </button>
              <button type="button" className="mkt-alert-modal-secondary" onClick={onClose} disabled={saving}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body,
  );
}

AgentModelModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.string,
  /** 'pipeline' = agent:model SSOT picker; 'app' = vendor-only select. */
  variant: PropTypes.oneOf(['pipeline', 'app']).isRequired,
  /** Pipeline only: [{ value, label }] from useAgentOptions(). */
  agentOptions: PropTypes.array,
  /** Pipeline: 'claude:opus-4.8'. App: 'claude' | 'codex'. */
  seedValue: PropTypes.string,
  /** Masked stored key, e.g. 'sk-a...wxyz', or ''. */
  seedKey: PropTypes.string,
  /** Pipeline+Claude only: masked stored OAuth token, or ''. */
  seedOAuth: PropTypes.string,
  /** Caller does the request. Receives the surface-specific body. */
  onSave: PropTypes.func.isRequired,
  /** Called after a successful onSave (refresh). */
  onSaved: PropTypes.func,
  setToast: PropTypes.func,
  /** Show the "also set as project default" opt-in (adds saveToProject to body). */
  showSaveToProject: PropTypes.bool,
  saveToProjectLabel: PropTypes.string,
  submitLabel: PropTypes.string,
};
